import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  Checkbox,
  FileInput,
  Group,
  Modal,
  ScrollArea,
  Stack,
  Text,
} from "@mantine/core";
import { importPimProducts } from "../pimApi";

export default function ImportProductsModal({ opened, onClose, targetTenantId, onImported }) {
  const { t } = useTranslation("pim");
  const [file, setFile] = useState(null);
  const [replaceExisting, setReplaceExisting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const handleClose = () => {
    setFile(null);
    setReplaceExisting(false);
    setResult(null);
    setError(null);
    setImporting(false);
    onClose();
  };

  const handleImport = async () => {
    if (!file || !targetTenantId) return;
    setImporting(true);
    setError(null);
    try {
      const text = await file.text();
      const exportData = JSON.parse(text);
      const importResult = await importPimProducts(exportData, replaceExisting);
      setResult(importResult);
      onImported?.();
    } catch (err) {
      setError(err.message || t("transfer.importFailed"));
    } finally {
      setImporting(false);
    }
  };

  return (
    <Modal opened={opened} onClose={handleClose} title={t("transfer.importProducts")} size="md">
      {result ? (
        <Stack>
          <Text>
            {t("transfer.importedInto", {
              schemas: result.importedSchemaNames?.length > 0
                ? result.importedSchemaNames.join(", ")
                : t("transfer.noSchemas"),
            })}
          </Text>
          <Text size="sm" c="dimmed">
            {t("transfer.productCounts", {
              created: result.productsCreated,
              updated: result.productsUpdated,
              variants: result.variantsImported,
              attributes: result.attributesImported,
              assets: result.assetsImported,
            })}
          </Text>
          {result.warnings?.length > 0 && (
            <Stack gap="xs">
              <Text size="sm" fw={600} c="orange">{t("transfer.warnings", { count: result.warnings.length })}</Text>
              <ScrollArea h={160}>
                <Stack gap={4}>
                  {result.warnings.map((w, i) => (
                    <Text key={i} size="xs" c="dimmed">• {w}</Text>
                  ))}
                </Stack>
              </ScrollArea>
            </Stack>
          )}
          <Group justify="flex-end">
            <Button onClick={handleClose}>{t("common.close")}</Button>
          </Group>
        </Stack>
      ) : (
        <Stack>
          <Text size="sm" c="dimmed">
            {t("transfer.productsHint")}
          </Text>
          <FileInput
            value={file}
            onChange={setFile}
            label={t("transfer.productFile")}
            placeholder={t("transfer.selectFile")}
            accept="application/json,.json"
            clearable
          />
          <Checkbox
            checked={replaceExisting}
            onChange={(e) => setReplaceExisting(e.currentTarget.checked)}
            label={t("transfer.overwriteProducts")}
            description={t("transfer.overwriteProductsHint")}
          />
          {error && <Text c="red" size="sm">{error}</Text>}
          <Group justify="flex-end">
            <Button variant="default" onClick={handleClose}>{t("common.cancel")}</Button>
            <Button onClick={handleImport} disabled={!file || !targetTenantId} loading={importing}>
              {t("transfer.import")}
            </Button>
          </Group>
        </Stack>
      )}
    </Modal>
  );
}
